import Button from '@atoms/Button/Button';
import ShadowContainerVertical from '@atoms/ShadowContainerVertical/ShadowContainerVertical';
import { IMessage } from '@models/IMessage';
import { IProductCommercialText } from '@models/IProductCommercialText';
import { ISharePanel } from '@models/ISharePanel';
import ProductDetailsInformationButton from '@molecules/ProductDetailsInformationButton/ProductDetailsInformationButton';
import SharePanel from '@molecules/SharePanel/SharePanel';
import updateDataLayer from '@utils/helpers/gtm';
import { useRef } from 'react';

interface Props {
  readonly commercialText: IProductCommercialText;
  readonly deliveryInformation: IMessage;
  readonly refurbishedInformation: IMessage;
  readonly shareOptions: ISharePanel;
  readonly dataTestId?: string;
}

const ProductDetailsInformation = ({
  commercialText,
  deliveryInformation,
  refurbishedInformation,
  shareOptions,
  dataTestId = 'Product-Details-Information',
}: Props) => {
  const sharePanelRef = useRef<HTMLDivElement>(null);

  return (
    <div
      className="relative flex flex-col rounded-[10px] bg-grey-100 py-3.5 lg:py-6"
      data-testid={dataTestId}
    >
      {deliveryInformation?.title && deliveryInformation?.text && (
        <ProductDetailsInformationButton
          onClick={() => {
            updateDataLayer({
              event: 'selectContent',
              content_type: 'product_support_info',
              link_text: deliveryInformation.title,
            });
          }}
          title={deliveryInformation.title}
          dataTestId={`${dataTestId}-Delivery-Info-Button`}
        >
          <div className="flex h-full flex-col gap-10 px-5 md:gap-7.5 lg:px-16">
            <p
              className="h5"
              data-testid={`${dataTestId}-Delivery-Info-Drawer-Title`}
            >
              {deliveryInformation.title}
            </p>
            <div
              className="body-two scrollbar-hide overflow-y-scroll content-area"
              dangerouslySetInnerHTML={{ __html: deliveryInformation.text }}
              data-testid={`${dataTestId}-Delivery-Info-Drawer-Text`}
            />
          </div>
        </ProductDetailsInformationButton>
      )}

      {refurbishedInformation?.title && refurbishedInformation?.text && (
        <ProductDetailsInformationButton
          onClick={() => {
            updateDataLayer({
              event: 'selectContent',
              content_type: 'product_support_info',
              link_text: refurbishedInformation.title,
            });
          }}
          title={refurbishedInformation.title}
          dataTestId={`${dataTestId}-Refurbished-Info-Button`}
        >
          <div className="flex h-full flex-col gap-10 px-5 md:gap-7.5 lg:px-16">
            <p
              className="h5"
              data-testid={`${dataTestId}-Refurbished-Info-Drawer-Title`}
            >
              {refurbishedInformation.title}
            </p>
            <div
              className="body-two scrollbar-hide overflow-y-scroll content-area"
              dangerouslySetInnerHTML={{
                __html: refurbishedInformation.text,
              }}
              data-testid={`${dataTestId}-Refurbished-Info-Drawer-Text`}
            />
          </div>
        </ProductDetailsInformationButton>
      )}

      {commercialText?.title && commercialText?.text && (
        <ProductDetailsInformationButton
          onClick={() => {
            updateDataLayer({
              event: 'selectContent',
              content_type: 'product_support_info',
              link_text: commercialText.title,
            });
          }}
          title={commercialText.title}
          subTitle={commercialText.subTitle}
          dataTestId={`${dataTestId}-Commercial-Info-Button`}
        >
          <div className="flex h-full flex-col items-stretch px-5 pb-16 lg:px-16">
            <p
              className="h5 mb-10 md:mb-7.5"
              data-testid={`${dataTestId}-Commercial-Info-Drawer-Title`}
            >
              {commercialText.title}
            </p>
            {commercialText.subTitle && (
              <div
                className="body-four mb-10 md:mb-7.5"
                data-testid={`${dataTestId}-Commercial-Info-Drawer-Subtitle`}
              >
                {commercialText.subTitle}
              </div>
            )}
            <ShadowContainerVertical
              shadowColour="from-grey-100"
              className="overflow-y-hidden"
              shadowHeight="h-28"
            >
              <div className="pb-10">
                <div
                  className="body-two content-area"
                  dangerouslySetInnerHTML={{ __html: commercialText.text }}
                  data-testid={`${dataTestId}-Commercial-Info-Drawer-Text`}
                />

                {commercialText.cta?.name && commercialText.cta?.url && (
                  <Button
                    className="my-7.5"
                    link={commercialText.cta}
                    data-testid={`${dataTestId}-Commercial-Info-Drawer-CTA`}
                  />
                )}
              </div>
            </ShadowContainerVertical>
          </div>
        </ProductDetailsInformationButton>
      )}

      <div ref={sharePanelRef} className="px-5 py-4">
        <SharePanel
          {...shareOptions}
          dataTestId={`${dataTestId}-Social-Share`}
        />
      </div>
    </div>
  );
};

export default ProductDetailsInformation;
